/**
 * Builds schema.org JSON-LD for marketing pages.
 *
 * Emits WebPage + BreadcrumbList, plus FAQPage when the page has an FAQ
 * section. Organization and WebSite live in public/index.html and are
 * referenced here by @id rather than duplicated.
 *
 * Usage:
 *   const schema = useMemo(() => buildMarketingPageSchema({
 *     path: '/ai-video-interview',
 *     name: 'AI Interview Practice',
 *     description: '…',
 *     faqs: FAQS.map(f => ({ question: f.q, answer: f.a })),
 *   }), []);
 *   usePageSEO({ …, jsonLd: schema });
 */

const BASE = 'https://marsresume.com';
const ORG_ID = `${BASE}/#organization`;
const SITE_ID = `${BASE}/#website`;

const clean = (str) => (str || '').replace(/\s+/g, ' ').trim();

export const buildMarketingPageSchema = ({
  path,
  name,
  description,
  faqs = [],
  breadcrumb = [],
  breadcrumbName,
  primaryImage,
}) => {
  if (!path) return [];

  const url = `${BASE}${path}`;
  const nodes = [];

  const webPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: clean(name),
    description: clean(description),
    inLanguage: 'en-US',
    isPartOf: { '@id': SITE_ID },
    about: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
  };

  if (primaryImage) {
    webPage.primaryImageOfPage = { '@type': 'ImageObject', url: primaryImage };
  }

  nodes.push(webPage);

  // Breadcrumbs render in the search result, so the leaf stays short even
  // when the page name carries a longer descriptive tagline.
  const trail = [
    { name: 'Home', item: `${BASE}/` },
    ...breadcrumb,
    { name: clean(breadcrumbName || name), item: url },
  ];

  nodes.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: trail.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: crumb.item,
    })),
  });

  // Google wants a meaningful set of Q&As, not a single stray entry.
  const usable = faqs.filter(f => clean(f.question) && clean(f.answer));
  if (usable.length >= 2) {
    nodes.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: usable.map(({ question, answer }) => ({
        '@type': 'Question',
        name: clean(question),
        acceptedAnswer: { '@type': 'Answer', text: clean(answer) },
      })),
    });
  }

  return nodes;
};
